import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { REPORT_ACCESS_PERMISSIONS } from '../../config/reportCapabilities';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  fileExists,
  openReportFile,
  shareReportFile,
} from '../../services/reportDownloadService';
import { PROJECT_TASK_REPORT_CODE } from '../../types/projectReport';
import { formatDateTime } from '../../utils/dateTime';
import { reportErrorMessage } from '../../utils/reportErrors';

type Props = NativeStackScreenProps<RootStackParamList, 'ReportDownloadResult'>;

function formatBytes(size: number | null): string {
  if (size == null || !Number.isFinite(size) || size < 0) return 'Unknown';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(value: string): string {
  const formatted = formatDateTime(value);
  return formatted === 'Not available' ? 'Unavailable' : formatted;
}

export function ReportDownloadResultScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      label: { ...typography.caption, color: palette.textMuted, marginTop: spacing.sm },
      value: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.body, color: palette.error, marginBottom: spacing.md },
    }),
    [palette],
  );

  const auth = useAuth();
  const downloads = useReportDownloads();
  const download = downloads.getDownload(route.params.downloadId);
  // Project task report files come from the Project module (PROJECT_VIEW), not
  // the Reports catalogue, so their owners may not hold REPORT_* permissions.
  const isProjectReport = download?.reportCode === PROJECT_TASK_REPORT_CODE;
  const allowed =
    auth.hasAnyPermission(REPORT_ACCESS_PERMISSIONS) ||
    (isProjectReport &&
      getProjectModuleCapabilities(auth.permissions, auth.roles)
        .canViewTaskReport);
  const backTitle = isProjectReport
    ? 'Back to Project Report'
    : 'Back to Reports';
  const goBackToSource = () =>
    isProjectReport
      ? navigation.goBack()
      : navigation.navigate(routes.reportList);
  const [exists, setExists] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      (async () => {
        if (!download) {
          if (active) setExists(false);
          return;
        }
        const present = await fileExists(download.localFilePath);
        if (active) setExists(present);
      })().catch(() => undefined);
      return () => {
        active = false;
      };
    }, [download]),
  );

  if (!download) {
    if (!allowed) return <UnauthorizedScreen />;
    return (
      <AppScreen>
        <ScreenHeader title="Download" onBack={navigation.goBack} />
        <View style={styles.body}>
          <Text accessibilityRole="alert" style={themed.error}>
            The downloaded report is no longer available.
          </Text>
          <AppButton
            title="Back to Reports"
            onPress={() => navigation.navigate(routes.reportList)}
          />
        </View>
      </AppScreen>
    );
  }

  if (!allowed) return <UnauthorizedScreen />;

  const open = async () => {
    setActionError(null);
    setBusy(true);
    try {
      await openReportFile({
        localFilePath: download.localFilePath,
        mimeType: download.mimeType,
        format: download.format,
        fileName: download.fileName,
      });
    } catch (error) {
      setActionError(
        reportErrorMessage(
          error,
          'No compatible application is available to open this report.',
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setActionError(null);
    setBusy(true);
    try {
      await shareReportFile({
        localFilePath: download.localFilePath,
        mimeType: download.mimeType,
        fileName: download.fileName,
        format: download.format,
      });
    } catch (error) {
      setActionError(
        reportErrorMessage(error, 'The report could not be shared.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setActionError(null);
    setBusy(true);
    try {
      await downloads.removeDownload(download.downloadId);
      goBackToSource();
    } catch (error) {
      setActionError(
        reportErrorMessage(error, 'The report could not be deleted.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const missing = !exists;

  return (
    <AppScreen scroll>
      <ScreenHeader title="Download ready" onBack={navigation.goBack} />
      <View style={styles.body}>
        <AppCard style={styles.card}>
          <Text style={themed.label}>Report</Text>
          <Text style={themed.value}>{download.reportName}</Text>
          <Text style={themed.label}>File name</Text>
          <Text style={themed.value}>{download.fileName}</Text>
          <Text style={themed.label}>Format</Text>
          <Text style={themed.value}>{download.format}</Text>
          <Text style={themed.label}>Downloaded</Text>
          <Text style={themed.value}>{formatTime(download.downloadedAt)}</Text>
          <Text style={themed.label}>Size</Text>
          <Text style={themed.value}>{formatBytes(download.sizeBytes)}</Text>
        </AppCard>

        {missing ? (
          <Text accessibilityRole="alert" style={themed.error}>
            The downloaded report is no longer available.
          </Text>
        ) : null}
        {actionError ? (
          <Text accessibilityRole="alert" style={themed.error}>
            {actionError}
          </Text>
        ) : null}

        <View style={styles.actions}>
          <AppButton
            title="Open"
            disabled={missing || busy}
            loading={busy}
            onPress={open}
          />
          <AppButton
            title="Share"
            variant="secondary"
            disabled={missing || busy}
            onPress={share}
          />
          <AppButton
            title="Delete"
            variant="danger"
            disabled={busy}
            onPress={remove}
          />
          <AppButton
            title={backTitle}
            variant="text"
            onPress={goBackToSource}
          />
        </View>
      </View>
    </AppScreen>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.xs, marginBottom: spacing.lg },
  actions: { gap: spacing.sm },
});
